import { useState, useEffect, useCallback } from 'react';
import { logger } from '@kuow/core';
import { 
  loadDoubleTheDonation, 
  getDtdContainer, 
  getDtdFieldValue,
  setDtdCompany,
  setDtdRoot,
} from './loadDtdScript';

export interface DtdCompany {
  id: string;
  status: string;
  enteredText: string;
  name: string;
}

interface UseDoubleTheDonationOptions {
  /** Initial company data */
  initialCompany?: DtdCompany | null;
  /** Shadow root when rendering inside a web component */
  shadowRoot?: ShadowRoot | null;
}

interface UseDoubleTheDonationReturn {
  company: DtdCompany | null;
  isLoaded: boolean;
  clearCompany: () => void;
}

/**
 * Hook to manage Double the Donation plugin integration
 * 
 * @param options - Configuration options including optional shadowRoot for widget mode
 */
export function useDoubleTheDonation(options?: UseDoubleTheDonationOptions): UseDoubleTheDonationReturn {
  const { initialCompany, shadowRoot } = options ?? {};
  const [company, setCompany] = useState<DtdCompany | null>(initialCompany ?? null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Check if the DTD plugin is initialized
  const checkInitialized = useCallback((): boolean => {
    return getDtdContainer() !== null;
  }, []);

  // Extract company data from DTD plugin fields
  const extractCompanyData = useCallback((): DtdCompany | null => {
    const companyId = getDtdFieldValue('doubledonation_company_id');
    
    if (!companyId) return null;

    return {
      id: companyId,
      status: getDtdFieldValue('doublethedonation_status') ?? '',
      enteredText: getDtdFieldValue('doublethedonation_entered_text') ?? '',
      name: getDtdFieldValue('doubledonation_company_name') ?? '',
    };
  }, []);

  // Monitor for company changes
  const monitorCompanyChanges = useCallback(() => {
    const intervalId = setInterval(() => {
      const currentCompany = extractCompanyData();
      const currentCompanyId = currentCompany?.id ?? null;
      const previousCompanyId = company?.id ?? null;

      if (currentCompanyId !== previousCompanyId) {
        setCompany(currentCompany);
        logger.info('DTD company changed', { company: currentCompany });
      }
    }, 200);

    return intervalId;
  }, [company, extractCompanyData]);

  // Clear the selected company
  const clearCompany = useCallback(() => {
    setDtdCompany(null);
    setCompany(null);
    logger.info('DTD company cleared');
  }, []);

  // Initialize DTD plugin
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    const initialize = async () => {
      try {
        // Set the root for DTD queries (document or shadow root)
        const root = shadowRoot ?? document;
        setDtdRoot(root);
        
        if (shadowRoot) {
          logger.info('DTD initializing with shadow root');
        }

        await loadDoubleTheDonation(root);
        
        // Wait for plugin to be fully initialized
        const checkAndStart = () => {
          if (checkInitialized()) {
            setIsLoaded(true);
            
            // Set initial company if provided
            if (initialCompany) {
              setDtdCompany(initialCompany.id);
            }
            
            // Start monitoring for changes
            intervalId = monitorCompanyChanges();
          } else {
            setTimeout(checkAndStart, 200);
          }
        };

        checkAndStart();
      } catch (error) {
        logger.error('Failed to initialize DTD plugin', { error });
      }
    };

    void initialize();

    // Cleanup
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [checkInitialized, monitorCompanyChanges, initialCompany, shadowRoot]);

  return {
    company,
    isLoaded,
    clearCompany,
  };
}

